export const FILTER_OPTIONS = {
  type: [
    { key: "basic-positive", label: "Basic positive" },
    { key: "inverted-positive", label: "Inverted positive" },
    { key: "basic-negative", label: "Basic negative" },
    { key: "inverted-negative", label: "Inverted negative" },
  ],
  strength: [
    { key: "strength-regular", label: "Regular" },
    { key: "strength-weak", label: "Weak" },
  ],
  weight: [
    { key: "weight-none", label: "None", value: "1" },
    { key: "weight-yes", label: "Yes", value: "0.5" },
  ],
  memory: [
    { key: "memory-without", label: "Without memory", value: "no" },
    { key: "memory-with", label: "With memory", value: "yes" },
  ],
  spontaneous: [
    { key: "spont-none", label: "None", value: "none" },
    { key: "spont-yes", label: "Yes", value: "1" },
    { key: "spont-increase", label: "Yes, increase only", value: "1" },
    { key: "spont-decrease", label: "Yes, decrease only", value: "0" },
  ],
  increment: [
    { key: "increment-prop", label: "Proportional to regulation" },
    { key: "increment-zero", label: "0 (set to 1 level per step)" },
    { key: "increment-other", label: "Other" },
  ],
  levels: [
    { key: "levels-2", label: "2", value: 2 },
    { key: "levels-3", label: "3", value: 3 },
    { key: "levels-4", label: "4", value: 4 },
    { key: "levels-5", label: "5", value: 5 },
  ],
};

/* Looks up display value for a group + key */
const fv = (group, key) => {
  const opt = FILTER_OPTIONS[group].find((o) => o.key === key);
  return opt?.value ?? opt?.label ?? "-";
};

/* Builds the card object — overrides layer on top of derived defaults */
export const makeCard = (motif, overrides = {}) => ({
  memoryVal: fv("memory", motif.memory),
  strengthVal: fv("strength", motif.strength),
  weightVal: fv("weight", motif.weight),
  biorecipe: {
    positive: "-",
    negative: "-",
    spontaneous: fv("spontaneous", motif.spontaneous),
    ...(overrides.biorecipe ?? {}),
  },
  dish: "",
  biological:
    "<<Textual description>>\n<<Element names assigned to variables in the motif above>>",
  ...overrides,
  // biorecipe already merged above, don't let spread overwrite it
  biorecipe: {
    positive: "-",
    negative: "-",
    spontaneous: fv("spontaneous", motif.spontaneous),
    ...(overrides.biorecipe ?? {}),
  },
});

/* ─────────────────────────────────────────────────────────────
   CARD OVERRIDES — unique fields per motif (description, rules, dish)
   Key = motif id
   ───────────────────────────────────────────────────────────── */
export const CARD_OVERRIDES = {
  bp_reg_none_nomem_spnone_prop: {
    description: "Rate of increase in A follows level of B",
    biorecipe: {
      positive: "B",
      negative: "-",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current + δ₁(B_current)",
  },
  //keep-check
  bp_reg_none_nomem_spdecrease_prop: {
    description:
      "Rate of increase in A follows level of B, when B≠0; otherwise, A decreases",
    biorecipe: {
      positive: "B",
      negative: "-",
      spontaneous: fv("spontaneous", "spont-yes"),
    },
    dish: "A_next = A_current + δ₁(B_current)",
  },
  //keep-check
  bp_reg_none_mem_spnone_prop: {
    description:
      "Rate of increase in A follows level of B",
    biorecipe: {
      positive: "B",
      negative: "-",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current + δ₁(B_current)",
  },
  bp_reg_wt_nomem_spnone_prop: {
    description: "Rate of increase in A follows level of weighted B",
    biorecipe: {
      positive: "0.5*B",
      negative: "-",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current + δ₁(0.5 · B_current)",
  },
  // #add
  bp_reg_wt_mem_spnone_prop: {
    description: "Rate of increase in A follows level of weighted B",
    biorecipe: {
      positive: "0.5*B",
      negative: "-",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current + δ₁(0.5 · B_current)",
  },
  bp_reg_wt_nomem_spdecrease_prop: {
    description:
      "Rate of increase in A follows level of weighted B, when B≠0; otherwise, A decreases",
    biorecipe: {
      positive: "0.5*B",
      negative: "-",
      spontaneous: fv("spontaneous", "spont-yes"),
    },
    dish: "A_next = A_current + δ₁(0.5 · B_current)",
  },
  // #add
  bp_reg_wt_mem_spdecrease_prop: {
    description:
      "Rate of increase in A follows level of weighted B, when B≠0; otherwise, A decreases",
    biorecipe: {
      positive: "0.5*B",
      negative: "-",
      spontaneous: fv("spontaneous", "spont-decrease"),
    },
    dish: "A_next = A_current + δ₁(0.5 · B_current)",
  },

  bp_weak_none_nomem_spnone_prop: {
    description:
      "Rate of increase in A follows level of B when B=highest level; otherwise, no change in A",
    biorecipe: {
      positive: "B^",
      negative: "-",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current + δ₁(⌊B_current⌋)",
  },
  // #add
  bp_weak_none_mem_spnone_prop: {
    description:
      "Rate of increase in A follows level of B when B=highest level; otherwise, no change in A",
    biorecipe: {
      positive: "B^",
      negative: "-",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current + δ₁(⌊B_current⌋)",
  },
  // #add

  bp_weak_none_nomem_spdecrease_prop: {
    description:
      "Rate of increase in A follows level of B when B=highest level; otherwise, A decreases",
    biorecipe: {
      positive: "B^",
      negative: "-",
      spontaneous: fv("spontaneous", "spont-yes"),
    },
    dish: "A_next = A_current + δ₁(⌊B_current⌋)",
  },
  // #add

  bp_weak_none_mem_spdecrease_prop: {
    description:
      "Rate of increase in A follows level of B when B=highest level; otherwise, A decreases",
    biorecipe: {
      positive: "B^",
      negative: "-",
      spontaneous: fv("spontaneous", "spont-decrease"),
    },
    dish: "A_next = A_current + δ₁(⌊B_current⌋)",
  },
  // #add

  bn_reg_none_nomem_spnone_prop: {
    description: "Rate of decrease in A follows level of B",
    biorecipe: {
      positive: "-",
      negative: "B",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current − δ₂(B_current)",
  },
  // #add

  bn_reg_none_mem_spnone_prop: {
    description: "Rate of decrease in A follows level of B",
    biorecipe: {
      positive: "-",
      negative: "B",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current − δ₂(B_current)",
  },
  // #add

  bn_reg_none_nomem_spincrease_prop: {
    description:
      "Rate of decrease in A follows level of B, when B≠0; otherwise, A increases",
    biorecipe: {
      positive: "-",
      negative: "B",
      spontaneous: fv("spontaneous", "spont-increase"),
    },
    dish: "A_next = A_current − δ₂(B_current)",
  },
  // #add

  bn_reg_none_mem_spincrease_prop: {
    description:
      "Rate of decrease in A follows level of B, when B≠0; otherwise, A increases",
    biorecipe: {
      positive: "-",
      negative: "B",
      spontaneous: fv("spontaneous", "spont-increase"),
    },
    dish: "A_next = A_current − δ₂(B_current)",
  },
  // #add

  bn_reg_wt_nomem_spnone_prop: {
    description: "Rate of decrease in A follows level of weighted B",
    biorecipe: {
      positive: "-",
      negative: "0.5*B",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current − δ₂(0.5 · B_current)",
  },
  // #add

  bn_reg_wt_mem_spnone_prop: {
    description: "Rate of decrease in A follows level of weighted B",
    biorecipe: {
      positive: "-",
      negative: "0.5*B",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current − δ₂(0.5 · B_current)",
  },
  // #add

  bn_reg_wt_nomem_spincrease_prop: {
    description:
      "Rate of decrease in A follows level of weighted B, when B≠0; otherwise, A increases",
    biorecipe: {
      positive: "-",
      negative: "0.5*B",
      spontaneous: fv("spontaneous", "spont-increase"),
    },
    dish: "A_next = A_current − δ₂(0.5 · B_current)",
  },
  // #add

  bn_reg_wt_mem_spincrease_prop: {
    description:
      "Rate of decrease in A follows level of weighted B, when B≠0; otherwise, A increases",
    biorecipe: {
      positive: "-",
      negative: "0.5*B",
      spontaneous: fv("spontaneous", "spont-increase"),
    },
    dish: "A_next = A_current − δ₂(0.5 · B_current)",
  },
  // #add

  bn_weak_none_nomem_spnone_prop: {
    description:
      "Rate of decrease in A follows level of B when B=highest level; otherwise, no change in A",
    biorecipe: {
      positive: "-",
      negative: "B^",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current − δ₂(⌊B_current⌋)",
  },
  // #add

  bn_weak_none_mem_spnone_prop: {
    description:
      "Rate of decrease in A follows level of B when B=highest level; otherwise, no change in A",
    biorecipe: {
      positive: "-",
      negative: "B^",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current − δ₂(⌊B_current⌋)",
  },
  // #add

  bn_weak_none_nomem_spincrease_prop: {
    description:
      "Rate of decrease in A follows level of B when B=highest level; otherwise, A increases",
    biorecipe: {
      positive: "-",
      negative: "B^",
      spontaneous: fv("spontaneous", "spont-increase"),
    },
    dish: "A_next = A_current − δ₂(⌊B_current⌋)",
  },
  // #add

  bn_weak_none_mem_spincrease_prop: {
    description:
      "Rate of decrease in A follows level of B when B=highest level; otherwise, A increases",
    biorecipe: {
      positive: "-",
      negative: "B^",
      spontaneous: fv("spontaneous", "spont-increase"),
    },
    dish: "A_next = A_current − δ₂(⌊B_current⌋)",
  },
  ip_reg_none_nomem_spnone_prop: {
    description: "Rate of increase in A follows complement of level of B",
    biorecipe: {
      positive: "!B",
      negative: "-",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current + δ₁(ᴺB_current)",
  },
  ip_reg_none_mem_spnone_prop: {
    description: "Rate of increase in A follows complement of level of B",
    biorecipe: {
      positive: "!B",
      negative: "-",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current + δ₁(ᴺB_current)",
  },
  ip_reg_none_nomem_spdecrease_prop: {
    description:
      "Rate of increase in A follows complement of level of B<highest level; otherwise, A decreases",
    biorecipe: {
      positive: "!B",
      negative: "-",
      spontaneous: fv("spontaneous", "spont-decrease"),
    },
    dish: "A_next = A_current + δ₁(ᴺB_current)",
  },
  ip_reg_none_mem_spdecrease_prop: {
    description:
      "Rate of increase in A follows complement of level of B<highest level; otherwise, A decreases",
    biorecipe: {
      positive: "!B",
      negative: "-",
      spontaneous: fv("spontaneous", "spont-decrease"),
    },
    dish: "A_next = A_current + δ₁(ᴺB_current)",
  },
  ip_reg_wt_nomem_spnone_prop: {
    description:
      "Rate of increase in A follows weighted complement of level of B",
    biorecipe: {
      positive: "0.5*!B",
      negative: "-",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current + δ₁(0.5 · ᴺB_current)",
  },
  ip_reg_wt_mem_spnone_prop: {
    description:
      "Rate of increase in A follows weighted complement of level of B",
    biorecipe: {
      positive: "0.5*!B",
      negative: "-",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current + δ₁(0.5 · ᴺB_current)",
  },
  ip_reg_wt_nomem_spdecrease_prop: {
    description:
      "Rate of increase in A follows weighted complement of level of B when B<highest level; otherwise, A decreases",
    biorecipe: {
      positive: "0.5*!B",
      negative: "-",
      spontaneous: fv("spontaneous", "spont-decrease"),
    },
    dish: "A_next = A_current + δ₁(0.5 · ᴺB_current)",
  },
  ip_reg_wt_mem_spdecrease_prop: {
    description:
      "Rate of increase in A follows weighted complement of level of B when B<highest level; otherwise, A decreases",
    biorecipe: {
      positive: "0.5*!B",
      negative: "-",
      spontaneous: fv("spontaneous", "spont-decrease"),
    },
    dish: "A_next = A_current + δ₁(0.5 · ᴺB_current)",
  },
  ip_weak_none_nomem_spnone_prop: {
    description:
      "Rate of increase in A follows complement of B=0 when B<highest level",
    biorecipe: {
      positive: "!B^",
      negative: "-",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current + δ₁(ᴺ⌊B_current⌋)",
  },
  ip_weak_none_mem_spnone_prop: {
    description:
      "Rate of increase in A follows complement of B=0 when B<highest level",
    biorecipe: {
      positive: "!B^",
      negative: "-",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current + δ₁(ᴺ⌊B_current⌋)",
  },
  ip_weak_none_nomem_spdecrease_prop: {
    description:
      "Rate of increase in A follows complement of B=0 when B<highest level; otherwise, A decreases",
    biorecipe: {
      positive: "!B^",
      negative: "-",
      spontaneous: fv("spontaneous", "spont-decrease"),
    },
    dish: "A_next = A_current + δ₁(ᴺ⌊B_current⌋)",
  },
  ip_weak_none_mem_spdecrease_prop: {
    description:
      "Rate of increase in A follows complement of B=0 when B<highest level; otherwise, A decreases",
    biorecipe: {
      positive: "!B^",
      negative: "-",
      spontaneous: fv("spontaneous", "spont-decrease"),
    },
    dish: "A_next = A_current + δ₁(ᴺ⌊B_current⌋)",
  },
  in_reg_none_nomem_spnone_prop: {
    description: "Rate of decrease in A follows complement of level of B",
    biorecipe: {
      positive: "-",
      negative: "!B",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current − δ₂(ᴺB_current)",
  },
  in_reg_none_mem_spnone_prop: {
    description: "Rate of decrease in A follows complement of level of B",
    biorecipe: {
      positive: "-",
      negative: "!B",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current − δ₂(ᴺB_current)",
  },
  in_reg_none_nomem_spincrease_prop: {
    description:
      "Rate of decrease in A follows complement of level of B<highest level; otherwise, A increases",
    biorecipe: {
      positive: "-",
      negative: "!B",
      spontaneous: fv("spontaneous", "spont-increase"),
    },
    dish: "A_next = A_current − δ₂(ᴺB_current)",
  },
  in_reg_none_mem_spincrease_prop: {
    description:
      "Rate of decrease in A follows complement of level of B<highest level; otherwise, A increases",
    biorecipe: {
      positive: "-",
      negative: "!B",
      spontaneous: fv("spontaneous", "spont-increase"),
    },
    dish: "A_next = A_current − δ₂(ᴺB_current)",
  },
  in_reg_wt_nomem_spnone_prop: {
    description:
      "Rate of decrease in A follows weighted complement of level of B",
    biorecipe: {
      positive: "-",
      negative: "0.5*!B",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current − δ₂(0.5 · ᴺB_current)",
  },
  in_reg_wt_mem_spnone_prop: {
    description:
      "Rate of decrease in A follows weighted complement of level of B",
    biorecipe: {
      positive: "-",
      negative: "0.5*!B",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current − δ₂(0.5 · ᴺB_current)",
  },
  in_reg_wt_nomem_spincrease_prop: {
    description:
      "Rate of decrease in A follows weighted complement of level of B when B<highest level; otherwise, A increases",
    biorecipe: {
      positive: "-",
      negative: "0.5*!B",
      spontaneous: fv("spontaneous", "spont-increase"),
    },
    dish: "A_next = A_current − δ₂(0.5 · ᴺB_current)",
  },
  in_reg_wt_mem_spincrease_prop: {
    description:
      "Rate of decrease in A follows weighted complement of level of B when B<highest level; otherwise, A increases",
    biorecipe: {
      positive: "-",
      negative: "0.5*!B",
      spontaneous: fv("spontaneous", "spont-increase"),
    },
    dish: "A_next = A_current − δ₂(0.5 · ᴺB_current)",
  },
  bp_reg_none_nomem_spnone_selfneg: {
    description: "A assumes the level of B",
    biorecipe: {
      positive: "B",
      negative: "A",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current + δ₁(B_current) − δ₂(A_current)",
  },
  bp_reg_none_mem_spnone_selfneg: {
    description: "A assumes the level of B",
    biorecipe: {
      positive: "B",
      negative: "A",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current + δ₁(B_current) − δ₂(A_current)",
  },
  bp_reg_none_nomem_spdecrease_selfneg: {
    description: "A oscillates between 0 and level of B",
    biorecipe: { positive: "B", negative: "A", spontaneous: "decrease,0" },
    dish: "A_next = A_current + δ₁(B_current) − δ₂(A_current)",
  },
  bp_reg_none_mem_spdecrease_selfneg: {
    description: "A oscillates between 0 and level of B",
    biorecipe: { positive: "B", negative: "A", spontaneous: "decrease,0" },
    dish: "A_next = A_current + δ₁(B_current) − δ₂(A_current)",
  },
  bp_reg_wt_nomem_spnone_selfneg: {
    description: "A oscillates around weighted B",
    biorecipe: {
      positive: "0.5*B",
      negative: "A",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current + δ₁(0.5 · B_current) − δ₂(A_current)",
  },
  bp_reg_wt_mem_spnone_selfneg: {
    description: "A oscillates around weighted B",
    biorecipe: {
      positive: "0.5*B",
      negative: "A",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current + δ₁(0.5 · B_current) − δ₂(A_current)",
  },
  bp_reg_wt_nomem_spdecrease_selfneg: {
    description: "A oscillates around weighted B",
    biorecipe: { positive: "0.5*B", negative: "A", spontaneous: "decrease,0" },
    dish: "A_next = A_current + δ₁(0.5 · B_current) − δ₂(A_current)",
  },
  bp_reg_wt_mem_spdecrease_selfneg: {
    description: "A oscillates around weighted B",
    biorecipe: { positive: "0.5*B", negative: "A", spontaneous: "decrease,0" },
    dish: "A_next = A_current + δ₁(0.5 · B_current) − δ₂(A_current)",
  },
  bp_weak_none_nomem_spnone_selfneg: {
    description:
      "A decreases to 0 when B<highest level; otherwise, A assumes level of B",
    biorecipe: {
      positive: "B^",
      negative: "A",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current + δ₁(⌊B_current⌋) − δ₂(A_current)",
  },
  bp_weak_none_mem_spnone_selfneg: {
    description:
      "A decreases to 0 when B<highest level; otherwise, A assumes level of B",
    biorecipe: {
      positive: "B^",
      negative: "A",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current + δ₁(⌊B_current⌋) − δ₂(A_current)",
  },
  bp_weak_none_nomem_spdecrease_selfneg: {
    description:
      "A decreases to 0 when B<highest level; otherwise, A oscillates between B and 0",
    biorecipe: { positive: "B^", negative: "A", spontaneous: "decrease,0" },
    dish: "A_next = A_current + δ₁(⌊B_current⌋) − δ₂(A_current)",
  },
  bp_weak_none_mem_spdecrease_selfneg: {
    description:
      "A decreases to 0 when B<highest level; otherwise, A oscillates between B and 0",
    biorecipe: { positive: "B^", negative: "A", spontaneous: "decrease,0" },
    dish: "A_next = A_current + δ₁(⌊B_current⌋) − δ₂(A_current)",
  },
  bn_reg_none_nomem_spnone_selfneg_or: {
    description: "A decreases to 0",
    biorecipe: {
      positive: "-",
      negative: "B,A",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current − δ₂(B_current, A_current)",
  },
  bn_reg_none_mem_spnone_selfneg_or: {
    description: "A decreases to 0",
    biorecipe: {
      positive: "-",
      negative: "B,A",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current − δ₂(B_current, A_current)",
  },
  bn_reg_none_nomem_spyes_selfneg_or: {
    description:
      "A decreases to 0 when B>0; otherwise, A oscillates between 0 and highest level",
    biorecipe: { positive: "-", negative: "B,A", spontaneous: "0" },
    dish: "A_next = A_current − δ₂(B_current, A_current)",
  },
  bn_reg_none_mem_spyes_selfneg_or: {
    description:
      "A decreases to 0 when B>0; otherwise, A oscillates between 0 and highest level",
    biorecipe: { positive: "-", negative: "B,A", spontaneous: "0" },
    dish: "A_next = A_current − δ₂(B_current, A_current)",
  },
  bn_reg_none_nomem_spnone_selfneg_and: {
    description: "Rate of decrease in A follows minimum of B and A",
    biorecipe: {
      positive: "-",
      negative: "(B,A)",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current − δ₂((B_current, A_current))",
  },
  bn_reg_none_mem_spnone_selfneg_and: {
    description: "Rate of decrease in A follows minimum of B and A",
    biorecipe: {
      positive: "-",
      negative: "(B,A)",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current − δ₂((B_current, A_current))",
  },
  bn_reg_none_nomem_spyes_selfneg_and: {
    description:
      "Rate of decrease in A follows minimum of B and A; A oscillates when B>0",
    biorecipe: { positive: "-", negative: "(B,A)", spontaneous: "0" },
    dish: "A_next = A_current − δ₂((B_current, A_current))",
  },
  bn_reg_none_mem_spyes_selfneg_and: {
    description:
      "Rate of decrease in A follows minimum of B and A; A oscillates when B>0",
    biorecipe: { positive: "-", negative: "(B,A)", spontaneous: "0" },
    dish: "A_next = A_current − δ₂((B_current, A_current))",
  },
  bn_reg_wt_nomem_spnone_selfneg_or: {
    description: "A decreases to 0",
    biorecipe: {
      positive: "-",
      negative: "0.5*B,A",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current − δ₂(0.5 · B_current, A_current)",
  },
  bn_reg_wt_mem_spnone_selfneg_or: {
    description: "A decreases to 0",
    biorecipe: {
      positive: "-",
      negative: "0.5*B,A",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current − δ₂(0.5 · B_current, A_current)",
  },
  bn_reg_wt_nomem_spyes_selfneg_or: {
    description:
      "A decreases to 0 when B>0; otherwise, A oscillates between 0 and highest level",
    biorecipe: { positive: "-", negative: "0.5*B,A", spontaneous: "0" },
    dish: "A_next = A_current − δ₂(0.5 · B_current, A_current)",
  },
  bn_reg_wt_mem_spyes_selfneg_or: {
    description:
      "A decreases to 0 when B>0; otherwise, A oscillates between 0 and highest level",
    biorecipe: { positive: "-", negative: "0.5*B,A", spontaneous: "0" },
    dish: "A_next = A_current − δ₂(0.5 · B_current, A_current)",
  },
  bn_reg_wt_nomem_spnone_selfneg_and: {
    description: "Rate of decrease in A follows minimum of weighted B and A",
    biorecipe: {
      positive: "-",
      negative: "(0.5*B,A)",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current − δ₂((0.5 · B_current, A_current))",
  },
  bn_reg_wt_mem_spnone_selfneg_and: {
    description: "Rate of decrease in A follows minimum of weighted B and A",
    biorecipe: {
      positive: "-",
      negative: "(0.5*B,A)",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current − δ₂((0.5 · B_current, A_current))",
  },
  bn_reg_wt_nomem_spyes_selfneg_and: {
    description:
      "A oscillates with decrease following the minimum of weighted B and A when B>0; otherwise, A increases",
    biorecipe: { positive: "-", negative: "(0.5*B,A)", spontaneous: "0" },
    dish: "A_next = A_current − δ₂((0.5 · B_current, A_current))",
  },
  bn_reg_wt_mem_spyes_selfneg_and: {
    description:
      "A oscillates with decrease following the minimum of weighted B and A when B>0; otherwise, A increases",
    biorecipe: { positive: "-", negative: "(0.5*B,A)", spontaneous: "0" },
    dish: "A_next = A_current − δ₂((0.5 · B_current, A_current))",
  },
  bn_weak_none_nomem_spnone_selfneg_or: {
    description: "A decreases to 0",
    biorecipe: {
      positive: "-",
      negative: "B^,A",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current − δ₂(⌊B_current⌋, A_current)",
  },
  bn_weak_none_mem_spnone_selfneg_or: {
    description: "A decreases to 0",
    biorecipe: {
      positive: "-",
      negative: "B^,A",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current − δ₂(⌊B_current⌋, A_current)",
  },
  bn_weak_none_nomem_spyes_selfneg_or: {
    description:
      "A decreases to 0 when B>0; otherwise, A oscillates between 0 and highest level",
    biorecipe: { positive: "-", negative: "B^,A", spontaneous: "0" },
    dish: "A_next = A_current − δ₂(⌊B_current⌋, A_current)",
  },
  bn_weak_none_mem_spyes_selfneg_or: {
    description:
      "A decreases to 0 when B>0; otherwise, A oscillates between 0 and highest level",
    biorecipe: { positive: "-", negative: "B^,A", spontaneous: "0" },
    dish: "A_next = A_current − δ₂(⌊B_current⌋, A_current)",
  },
  bn_weak_none_nomem_spnone_selfneg_and: {
    description:
      "A decreases to 0 when B=highest level; otherwise, A doesn't change",
    biorecipe: {
      positive: "-",
      negative: "(B^,A)",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current − δ₂((⌊B_current⌋, A_current))",
  },
  bn_weak_none_mem_spnone_selfneg_and: {
    description:
      "A decreases to 0 when B=highest level; otherwise, A doesn't change",
    biorecipe: {
      positive: "-",
      negative: "(B^,A)",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current − δ₂((⌊B_current⌋, A_current))",
  },
  bn_weak_none_nomem_spyes_selfneg_and: {
    description: "A oscillates when B=highest level; otherwise, A increases",
    biorecipe: { positive: "-", negative: "(B^,A)", spontaneous: "0" },
    dish: "A_next = A_current − δ₂((⌊B_current⌋, A_current))",
  },
  bn_weak_none_mem_spyes_selfneg_and: {
    description: "A oscillates when B=highest level; otherwise, A increases",
    biorecipe: { positive: "-", negative: "(B^,A)", spontaneous: "0" },
    dish: "A_next = A_current − δ₂((⌊B_current⌋, A_current))",
  },
  ip_reg_none_nomem_spnone_selfneg: {
    description: "A assumes the level of complement of B",
    biorecipe: {
      positive: "!B",
      negative: "A",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current + δ₁(ᴺB_current) − δ₂(A_current)",
  },
  ip_reg_none_mem_spnone_selfneg: {
    description: "A assumes the level of complement of B",
    biorecipe: {
      positive: "!B",
      negative: "A",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current + δ₁(ᴺB_current) − δ₂(A_current)",
  },
  ip_reg_none_nomem_spdecrease_selfneg: {
    description: "A oscillates between 0 and level of complement of B",
    biorecipe: { positive: "!B", negative: "A", spontaneous: "decrease,0" },
    dish: "A_next = A_current + δ₁(ᴺB_current) − δ₂(A_current)",
  },
  ip_reg_none_mem_spdecrease_selfneg: {
    description: "A oscillates between 0 and level of complement of B",
    biorecipe: { positive: "!B", negative: "A", spontaneous: "decrease,0" },
    dish: "A_next = A_current + δ₁(ᴺB_current) − δ₂(A_current)",
  },
  ip_reg_wt_nomem_spnone_selfneg: {
    description: "A oscillates around weighted complement of B",
    biorecipe: {
      positive: "0.5*!B",
      negative: "A",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current + δ₁(0.5 · ᴺB_current) − δ₂(A_current)",
  },
  ip_reg_wt_mem_spnone_selfneg: {
    description: "A oscillates around weighted complement of B",
    biorecipe: {
      positive: "0.5*!B",
      negative: "A",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current + δ₁(0.5 · ᴺB_current) − δ₂(A_current)",
  },
  ip_reg_wt_nomem_spdecrease_selfneg: {
    description: "A oscillates around weighted complement of B",
    biorecipe: { positive: "0.5*!B", negative: "A", spontaneous: "decrease,0" },
    dish: "A_next = A_current + δ₁(0.5 · ᴺB_current) − δ₂(A_current)",
  },
  ip_reg_wt_mem_spdecrease_selfneg: {
    description: "A oscillates around weighted complement of B",
    biorecipe: { positive: "0.5*!B", negative: "A", spontaneous: "decrease,0" },
    dish: "A_next = A_current + δ₁(0.5 · ᴺB_current) − δ₂(A_current)",
  },
  ip_weak_none_nomem_spnone_selfneg: {
    description:
      "A decreases to 0 when B>0; otherwise, A assumes level of complement of B",
    biorecipe: {
      positive: "!B^",
      negative: "A",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current + δ₁(⌊ᴺB_current⌋) − δ₂(A_current)",
  },
  ip_weak_none_mem_spnone_selfneg: {
    description:
      "A decreases to 0 when B>0; otherwise, A assumes level of complement of B",
    biorecipe: {
      positive: "!B^",
      negative: "A",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current + δ₁(⌊ᴺB_current⌋) − δ₂(A_current)",
  },
  ip_weak_none_nomem_spdecrease_selfneg: {
    description:
      "A decreases to 0 when B>0; otherwise, A oscillates between B and 0",
    biorecipe: { positive: "!B^", negative: "A", spontaneous: "decrease,0" },
    dish: "A_next = A_current + δ₁(⌊ᴺB_current⌋) − δ₂(A_current)",
  },
  ip_weak_none_mem_spdecrease_selfneg: {
    description:
      "A decreases to 0 when B>0; otherwise, A oscillates between B and 0",
    biorecipe: { positive: "!B^", negative: "A", spontaneous: "decrease,0" },
    dish: "A_next = A_current + δ₁(⌊ᴺB_current⌋) − δ₂(A_current)",
  },
  in_reg_none_nomem_spnone_selfneg_or: {
    description: "A decreases to 0",
    biorecipe: {
      positive: "-",
      negative: "!B,A",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current − δ₂(ᴺB_current, A_current)",
  },
  in_reg_none_mem_spnone_selfneg_or: {
    description: "A decreases to 0",
    biorecipe: {
      positive: "-",
      negative: "!B,A",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current − δ₂(ᴺB_current, A_current)",
  },
  in_reg_none_nomem_spyes_selfneg_or: {
    description:
      "A decreases to 0 when B<highest level; otherwise, A oscillates between 0 and highest level",
    biorecipe: { positive: "-", negative: "!B,A", spontaneous: "0" },
    dish: "A_next = A_current − δ₂(ᴺB_current, A_current)",
  },
  in_reg_none_mem_spyes_selfneg_or: {
    description:
      "A decreases to 0 when B<highest level; otherwise, A oscillates between 0 and highest level",
    biorecipe: { positive: "-", negative: "!B,A", spontaneous: "0" },
    dish: "A_next = A_current − δ₂(ᴺB_current, A_current)",
  },
  in_reg_none_nomem_spnone_selfneg_and: {
    description:
      "Rate of decrease in A follows minimum of complement of B and A",
    biorecipe: {
      positive: "-",
      negative: "(!B,A)",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current − δ₂((ᴺB_current, A_current))",
  },
  in_reg_none_mem_spnone_selfneg_and: {
    description:
      "Rate of decrease in A follows minimum of complement of B and A",
    biorecipe: {
      positive: "-",
      negative: "(!B,A)",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current − δ₂((ᴺB_current, A_current))",
  },
  in_reg_none_nomem_spyes_selfneg_and: {
    description:
      "Rate of decrease in A follows minimum of complement of B and A; A oscillates when B<highest level",
    biorecipe: { positive: "-", negative: "(!B,A)", spontaneous: "0" },
    dish: "A_next = A_current − δ₂((ᴺB_current, A_current))",
  },
  in_reg_none_mem_spyes_selfneg_and: {
    description:
      "Rate of decrease in A follows minimum of complement of B and A; A oscillates when B<highest level",
    biorecipe: { positive: "-", negative: "(!B,A)", spontaneous: "0" },
    dish: "A_next = A_current − δ₂((ᴺB_current, A_current))",
  },
  in_reg_wt_nomem_spnone_selfneg_or: {
    description: "A decreases to 0",
    biorecipe: {
      positive: "-",
      negative: "0.5*!B,A",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current − δ₂(0.5 · ᴺB_current, A_current)",
  },
  in_reg_wt_mem_spnone_selfneg_or: {
    description: "A decreases to 0",
    biorecipe: {
      positive: "-",
      negative: "0.5*!B,A",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current − δ₂(0.5 · ᴺB_current, A_current)",
  },
  in_reg_wt_nomem_spyes_selfneg_or: {
    description:
      "A decreases to 0 when B<highest level; otherwise, A oscillates between 0 and highest level",
    biorecipe: { positive: "-", negative: "0.5*!B,A", spontaneous: "0" },
    dish: "A_next = A_current − δ₂(0.5 · ᴺB_current, A_current)",
  },
  in_reg_wt_mem_spyes_selfneg_or: {
    description:
      "A decreases to 0 when B<highest level; otherwise, A oscillates between 0 and highest level",
    biorecipe: { positive: "-", negative: "0.5*!B,A", spontaneous: "0" },
    dish: "A_next = A_current − δ₂(0.5 · ᴺB_current, A_current)",
  },
  in_reg_wt_nomem_spnone_selfneg_and: {
    description:
      "Rate of decrease in A follows minimum of weighted complement of B and A",
    biorecipe: {
      positive: "-",
      negative: "(0.5*!B,A)",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current − δ₂((0.5 · ᴺB_current, A_current))",
  },
  in_reg_wt_mem_spnone_selfneg_and: {
    description:
      "Rate of decrease in A follows minimum of weighted complement of B and A",
    biorecipe: {
      positive: "-",
      negative: "(0.5*!B,A)",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current − δ₂((0.5 · ᴺB_current, A_current))",
  },
  in_reg_wt_nomem_spyes_selfneg_and: {
    description:
      "A oscillates with decrease following the minimum of weighted complement of B and A when B<highest level; otherwise, A increases",
    biorecipe: { positive: "-", negative: "(0.5*!B,A)", spontaneous: "0" },
    dish: "A_next = A_current − δ₂((0.5 · ᴺB_current, A_current))",
  },
  in_reg_wt_mem_spyes_selfneg_and: {
    description:
      "A oscillates with decrease following the minimum of weighted complement of B and A when B<highest level; otherwise, A increases",
    biorecipe: { positive: "-", negative: "(0.5*!B,A)", spontaneous: "0" },
    dish: "A_next = A_current − δ₂((0.5 · ᴺB_current, A_current))",
  },
  in_weak_none_nomem_spnone_selfneg_or: {
    description: "A decreases to 0",
    biorecipe: {
      positive: "-",
      negative: "!B^,A",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current − δ₂(ᴺ⌊B_current⌋, A_current)",
  },
  in_weak_none_mem_spnone_selfneg_or: {
    description: "A decreases to 0",
    biorecipe: {
      positive: "-",
      negative: "!B^,A",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current − δ₂(ᴺ⌊B_current⌋, A_current)",
  },
  in_weak_none_nomem_spyes_selfneg_or: {
    description:
      "A decreases to 0 when B<highest level; otherwise, A oscillates between 0 and highest level",
    biorecipe: { positive: "-", negative: "!B^,A", spontaneous: "0" },
    dish: "A_next = A_current − δ₂(ᴺ⌊B_current⌋, A_current)",
  },
  in_weak_none_mem_spyes_selfneg_or: {
    description:
      "A decreases to 0 when B<highest level; otherwise, A oscillates between 0 and highest level",
    biorecipe: { positive: "-", negative: "!B^,A", spontaneous: "0" },
    dish: "A_next = A_current − δ₂(ᴺ⌊B_current⌋, A_current)",
  },
  in_weak_none_nomem_spnone_selfneg_and: {
    description:
      "A decreases to 0 when B<highest level; otherwise, A doesn't change",
    biorecipe: {
      positive: "-",
      negative: "(!B^,A)",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current − δ₂((ᴺ⌊B_current⌋, A_current))",
  },
  in_weak_none_mem_spnone_selfneg_and: {
    description:
      "A decreases to 0 when B<highest level; otherwise, A doesn't change",
    biorecipe: {
      positive: "-",
      negative: "(!B^,A)",
      spontaneous: fv("spontaneous", "spont-none"),
    },
    dish: "A_next = A_current − δ₂((ᴺ⌊B_current⌋, A_current))",
  },
  in_weak_none_nomem_spyes_selfneg_and: {
    description:
      "A oscillates with decrease following the minimum of complement of B and A when B<highest level; otherwise, A increases",
    biorecipe: { positive: "-", negative: "(!B^,A)", spontaneous: "0" },
    dish: "A_next = A_current − δ₂((ᴺ⌊B_current⌋, A_current))",
  },
  in_weak_none_mem_spyes_selfneg_and: {
    description:
      "A oscillates with decrease following the minimum of complement of B and A when B<highest level; otherwise, A increases",
    biorecipe: { positive: "-", negative: "(!B^,A)", spontaneous: "0" },
    dish: "A_next = A_current − δ₂((ᴺ⌊B_current⌋, A_current))",
  },
};

/* ─────────────────────────────────────────────────────────────
   MOTIFS — filter keys only; card is derived via makeCard
   ───────────────────────────────────────────────────────────── */
export const MOTIFS = [
  // ── Basic positive ───────────────────────────────
  {
    // checked
    id: "bp_reg_none_mem_spnone_prop",
    label: "Single positive",
    type: "basic-positive",
    strength: "strength-regular",
    weight: "weight-none",
    memory: "memory-with",
    spontaneous: "spont-none",
    increment: "increment-prop",
  },
  {
    // checked
    id: "bp_reg_none_mem_spyes_prop",
    label: "Single positive w/ spontaneous decrease",
    type: "basic-positive",
    strength: "strength-regular",
    weight: "weight-none",
    memory: "memory-with",
    spontaneous: "spont-yes",
    increment: "increment-prop",
  },

  {
    // checked

    id: "bp_reg_wt_mem_spnone_prop",
    label: "Single weighted positive",
    type: "basic-positive",
    strength: "strength-regular",
    weight: "weight-yes",
    memory: "memory-with",
    spontaneous: "spont-none",
    increment: "increment-prop",
  },
  {
    // checked
    id: "bp_reg_wt_mem_spdecrease_prop",
    label: "Single weighted positive w/ spontaneous decrease",
    type: "basic-positive",
    strength: "strength-regular",
    weight: "weight-yes",
    memory: "memory-with",
    spontaneous: "spont-yes",
    increment: "increment-prop",
  },
  //
  {
    // checked
    id: "bp_weak_none_mem_spnone_prop",
    label: "Single weak positive",
    type: "basic-positive",
    strength: "strength-weak",
    weight: "weight-none",
    memory: "memory-with",
    spontaneous: "spont-none",
    increment: "increment-prop",
  },
  {
    // checked
    id: "bp_weak_none_mem_spyes_prop",
    label: "Single weak positive w/ spontaneous decrease",
    type: "basic-positive",
    strength: "strength-weak",
    weight: "weight-none",
    memory: "memory-with",
    spontaneous: "spont-yes",
    increment: "increment-prop",
  },

  {
    // checked
    id: "bp_reg_none_nomem_spnone_selfneg",
    label: "Single positive w/ negative self-regulation",
    type: "basic-positive",
    strength: "strength-regular",
    weight: "weight-none",
    memory: "memory-without",
    spontaneous: "spont-none",
    increment: "increment-prop",
  },

  {
    // checked
    id: "bp_reg_none_nomem_spdecrease_selfneg",
    label:
      "Single positive w/ negative self-regulation and w/ unbalancing decrease",
    type: "basic-positive",
    strength: "strength-regular",
    weight: "weight-none",
    memory: "memory-without",
    spontaneous: "spont-decrease",
    increment: "increment-prop",
  },
  {
    // check
    id: "bp_reg_wt_nomem_spnone_selfneg",
    label: "Single weighted positive w/ negative self-regulation",
    type: "basic-positive",
    strength: "strength-regular",
    weight: "weight-yes",
    memory: "memory-without",
    spontaneous: "spont-none",
    increment: "increment-prop",
  },
  {
    // checked
    id: "bp_weak_none_nomem_spnone_selfneg",
    label: "Single weak positive w/ negative self-regulation",
    type: "basic-positive",
    strength: "strength-weak",
    weight: "weight-none",
    memory: "memory-without",
    spontaneous: "spont-none",
    increment: "increment-prop",
  },

  

  // ── Basic negative ─────────────────────────────

  {
    // checked
    id: "bn_reg_none_mem_spnone_prop",
    label: "Single negative",
    type: "basic-negative",
    strength: "strength-regular",
    weight: "weight-none",
    memory: "memory-with",
    spontaneous: "spont-none",
    increment: "increment-prop",
  },
  {
    // checked

    id: "bn_reg_none_mem_spincrease_prop",
    label: "Single negative w/ spontaneous increase",
    type: "basic-negative",
    strength: "strength-regular",
    weight: "weight-none",
    memory: "memory-with",
    spontaneous: "spont-yes",
    increment: "increment-prop",
  },
  {
    // checked
    id: "bn_reg_wt_nomem_spnone_prop",
    label: "Single weighted negative",
    type: "basic-negative",
    strength: "strength-regular",
    weight: "weight-yes",
    memory: "memory-with",
    spontaneous: "spont-none",
    increment: "increment-prop",
  },

  {
    // checked
    id: "bn_reg_wt_mem_spincrease_prop",
    label: "Single weighted negative w/ spontaneous increase",
    type: "basic-negative",
    strength: "strength-regular",
    weight: "weight-yes",
    memory: "memory-with",
    spontaneous: "spont-yes",
    increment: "increment-prop",
  },
  {
    // checked
    id: "bn_weak_none_nomem_spnone_prop",
    label: "Single weak negative",
    type: "basic-negative",
    strength: "strength-weak",
    weight: "weight-none",
    memory: "memory-with",
    spontaneous: "spont-none",
    increment: "increment-prop",
  },

  {
    // checked
    id: "bn_weak_none_mem_spincrease_prop",
    label: "Single weak negative w/ spontaneous decrease",
    type: "basic-negative",
    strength: "strength-weak",
    weight: "weight-none",
    memory: "memory-with",
    spontaneous: "spont-yes",
    increment: "increment-prop",
  },

  {
    // checked
    id: "bn_reg_none_nomem_spnone_selfneg_or",
    label: "Single negative w/ negative self-regulation (OR)",
    type: "basic-negative",
    strength: "strength-regular",
    weight: "weight-none",
    memory: "memory-without",
    spontaneous: "spont-none",
    increment: "increment-prop",
  },

  {
    // checked
    id: "bn_reg_none_nomem_spyes_selfneg_or",
    label:
      "Single negative w/ negative self-regulation (OR) and w/ spontaneous",
    type: "basic-negative",
    strength: "strength-regular",
    weight: "weight-none",
    memory: "memory-without",
    spontaneous: "spont-yes",
    increment: "increment-prop",
  },

  {
    // checked
    id: "bn_reg_none_mem_spnone_selfneg_and",
    label: "Single negative w/ negative self-regulation (AND)",
    type: "basic-negative",
    strength: "strength-regular",
    weight: "weight-none",
    memory: "memory-with",
    spontaneous: "spont-none",
    increment: "increment-prop",
  },

  {
    // checked
    id: "bn_reg_none_mem_spyes_selfneg_and",
    label:
      "Single negative w/ negative self-regulation (AND) and w/ spontaneous",
    type: "basic-negative",
    strength: "strength-regular",
    weight: "weight-none",
    memory: "memory-with",
    spontaneous: "spont-yes",
    increment: "increment-prop",
  },
  {
    // checked

    id: "bn_reg_wt_nomem_spnone_selfneg_or",
    label: "Single weighted negative w/ negative self-regulation (OR)",
    type: "basic-negative",
    strength: "strength-regular",
    weight: "weight-yes",
    memory: "memory-without",
    spontaneous: "spont-none",
    increment: "increment-prop",
  },

  {
    // checked
    id: "bn_reg_wt_mem_spyes_selfneg_or",
    label:
      "Single weighted negative w/ negative self-regulation (OR) and w/ spontaneous",
    type: "basic-negative",
    strength: "strength-regular",
    weight: "weight-yes",
    memory: "memory-with",
    spontaneous: "spont-yes",
    increment: "increment-prop",
  },

  {
    // checked
    id: "bn_reg_wt_mem_spnone_selfneg_and",
    label: "Single weighted negative w/ negative self-regulation (AND)",
    type: "basic-negative",
    strength: "strength-regular",
    weight: "weight-yes",
    memory: "memory-with",
    spontaneous: "spont-none",
    increment: "increment-prop",
  },

  {
    // checked
    id: "bn_reg_wt_mem_spyes_selfneg_and",
    label:
      "Single weighted negative w/ negative self-regulation (AND) and w/ spontaneous",
    type: "basic-negative",
    strength: "strength-regular",
    weight: "weight-yes",
    memory: "memory-with",
    spontaneous: "spont-yes",
    increment: "increment-prop",
  },
  {
    // checked
    id: "bn_weak_none_nomem_spnone_selfneg_or",
    label: "Single weak negative w/ negative self-regulation (OR)",
    type: "basic-negative",
    strength: "strength-weak",
    weight: "weight-none",
    memory: "memory-without",
    spontaneous: "spont-none",
    increment: "increment-prop",
  },

  {
    // checked
    id: "bn_weak_none_mem_spyes_selfneg_or",
    label:
      "Single weak negative w/ negative self-regulation (OR) and w/ spontaneous",
    type: "basic-negative",
    strength: "strength-weak",
    weight: "weight-none",
    memory: "memory-with",
    spontaneous: "spont-yes",
    increment: "increment-prop",
  },

  {
    // checked
    id: "bn_weak_none_nomem_spnone_selfneg_and",
    label: "Single weak negative w/ negative self-regulation (AND)",
    type: "basic-negative",
    strength: "strength-weak",
    weight: "weight-none",
    memory: "memory-without",
    spontaneous: "spont-none",
    increment: "increment-prop",
  },

  {
    // checked
    id: "bn_weak_none_mem_spyes_selfneg_and",
    label:
      "Single weak negative w/ negative self-regulation (AND) and w/ spontaneous",
    type: "basic-negative",
    strength: "strength-weak",
    weight: "weight-none",
    memory: "memory-with",
    spontaneous: "spont-yes",
    increment: "increment-prop",
  },
  // ── Inverted positive

  {
    // checked
    id: "ip_reg_none_mem_spnone_prop",
    label: "Single inverted positive",
    type: "inverted-positive",
    strength: "strength-regular",
    weight: "weight-none",
    memory: "memory-with",
    spontaneous: "spont-none",
    increment: "increment-prop",
  },

  {
    // checked
    id: "ip_reg_none_mem_spdecrease_prop",
    label: "Single inverted positive w/ spontaneous decrease",
    type: "inverted-positive",
    strength: "strength-regular",
    weight: "weight-none",
    memory: "memory-with",
    spontaneous: "spont-yes",
    increment: "increment-prop",
  },

  {
    // checked
    id: "ip_reg_wt_mem_spnone_prop",
    label: "Single weighted inverted positive",
    type: "inverted-positive",
    strength: "strength-regular",
    weight: "weight-yes",
    memory: "memory-with",
    spontaneous: "spont-none",
    increment: "increment-prop",
  },

  {
    // checked
    id: "ip_reg_wt_mem_spdecrease_prop",
    label: "Single weighted inverted positive w/ spontaneous decrease",
    type: "inverted-positive",
    strength: "strength-regular",
    weight: "weight-yes",
    memory: "memory-with",
    spontaneous: "spont-yes",
    increment: "increment-prop",
  },

  {
    // checked
    id: "ip_weak_none_mem_spnone_prop",
    label: "Single weak inverted positive",
    type: "inverted-positive",
    strength: "strength-weak",
    weight: "weight-none",
    memory: "memory-with",
    spontaneous: "spont-none",
    increment: "increment-prop",
  },

  {
    // checked
    id: "ip_weak_none_mem_spdecrease_prop",
    label: "Single weak inverted positive w/ spontaneous decrease",
    type: "inverted-positive",
    strength: "strength-weak",
    weight: "weight-none",
    memory: "memory-with",
    spontaneous: "spont-yes",
    increment: "increment-prop",
  },

  {
    // checked
    id: "ip_reg_none_nomem_spnone_selfneg",
    label: "Single inverted positive w/ negative self-regulation",
    type: "inverted-positive",
    strength: "strength-regular",
    weight: "weight-none",
    memory: "memory-without",
    spontaneous: "spont-none",
    increment: "increment-prop",
  },

  {
    // checked
    id: "ip_reg_none_nomem_spdecrease_selfneg",
    label:
      "Single inverted positive w/ negative self-regulation and w/ unbalancing decrease",
    type: "inverted-positive",
    strength: "strength-regular",
    weight: "weight-none",
    memory: "memory-without",
    spontaneous: "spont-decrease",
    increment: "increment-prop",
  },

  {
    // checked
    id: "ip_reg_wt_nomem_spnone_selfneg",
    label: "Single weighted inverted positive w/ negative self-regulation",
    type: "inverted-positive",
    strength: "strength-regular",
    weight: "weight-yes",
    memory: "memory-without",
    spontaneous: "spont-none",
    increment: "increment-prop",
  },

  {
    // checked
    id: "ip_reg_wt_mem_spdecrease_selfneg",
    label:
      "Single weighted inverted positive w/ negative self-regulation and w/ unbalancing decrease",
    type: "inverted-positive",
    strength: "strength-regular",
    weight: "weight-yes",
    memory: "memory-without",
    spontaneous: "spont-decrease",
    increment: "increment-prop",
  },
  {
    // checked
    id: "ip_weak_none_nomem_spnone_selfneg",
    label: "Single weak inverted positive w/ negative self-regulation",
    type: "inverted-positive",
    strength: "strength-weak",
    weight: "weight-none",
    memory: "memory-without",
    spontaneous: "spont-none",
    increment: "increment-prop",
  },

  {
    // checked
    id: "ip_weak_none_nomem_spdecrease_selfneg",
    label:
      "Single weak inverted positive w/ negative self-regulation and w/ unbalancing decrease",
    type: "inverted-positive",
    strength: "strength-weak",
    weight: "weight-none",
    memory: "memory-without",
    spontaneous: "spont-decrease",
    increment: "increment-prop",
  },

  // Inverted negative w/ memory and neg. self-regulation

  {
    // checked
    id: "in_reg_none_mem_spnone_prop",
    label: "Single inverted negative",
    type: "inverted-negative",
    strength: "strength-regular",
    weight: "weight-none",
    memory: "memory-with",
    spontaneous: "spont-none",
    increment: "increment-prop",
  },

  {
    // checked
    id: "in_reg_none_mem_spincrease_prop",
    label: "Single inverted negative w/ spontaneous increase",
    type: "inverted-negative",
    strength: "strength-regular",
    weight: "weight-none",
    memory: "memory-with",
    spontaneous: "spont-yes",
    increment: "increment-prop",
  },

  {
    // checked
    id: "in_reg_wt_mem_spnone_prop",
    label: "Single weighted inverted negative",
    type: "inverted-negative",
    strength: "strength-regular",
    weight: "weight-yes",
    memory: "memory-with",
    spontaneous: "spont-none",
    increment: "increment-prop",
  },

  {
    // checked
    id: "in_reg_wt_mem_spincrease_prop",
    label: "Single weighted inverted negative w/ spontaneous increase",
    type: "inverted-negative",
    strength: "strength-regular",
    weight: "weight-yes",
    memory: "memory-with",
    spontaneous: "spont-yes",
    increment: "increment-prop",
  },

  {
    // checked
    id: "in_reg_none_nomem_spnone_selfneg_or",
    label: "Single inverted negative w/ negative self-regulation (OR)",
    type: "inverted-negative",
    strength: "strength-regular",
    weight: "weight-none",
    memory: "memory-without",
    spontaneous: "spont-none",
    increment: "increment-prop",
  },

  {
    // checked
    id: "in_reg_none_nomem_spyes_selfneg_or",
    label:
      "Single inverted negative w/ negative self-regulation (OR) and w/ spontaneous",
    type: "inverted-negative",
    strength: "strength-regular",
    weight: "weight-none",
    memory: "memory-without",
    spontaneous: "spont-yes",
    increment: "increment-prop",
  },

  {
    // checked
    id: "in_reg_none_mem_spnone_selfneg_and",
    label: "Single inverted negative w/ negative self-regulation (AND)",
    type: "inverted-negative",
    strength: "strength-regular",
    weight: "weight-none",
    memory: "memory-with",
    spontaneous: "spont-none",
    increment: "increment-prop",
  },

  {
    // checked
    id: "in_reg_none_mem_spyes_selfneg_and",
    label:
      "Single inverted negative w/ negative self-regulation (AND) and w/ spontaneous",
    type: "inverted-negative",
    strength: "strength-regular",
    weight: "weight-none",
    memory: "memory-with",
    spontaneous: "spont-yes",
    increment: "increment-prop",
  },
  {
    // checked
    id: "in_reg_wt_nomem_spnone_selfneg_or",
    label: "Single weighted inverted negative w/ negative self-regulation (OR)",
    type: "inverted-negative",
    strength: "strength-regular",
    weight: "weight-yes",
    memory: "memory-without",
    spontaneous: "spont-none",
    increment: "increment-prop",
  },

  {
    // checked
    id: "in_reg_wt_mem_spyes_selfneg_or",
    label:
      "Single weighted inverted negative w/ negative self-regulation (OR) and w/ spontaneous",
    type: "inverted-negative",
    strength: "strength-regular",
    weight: "weight-yes",
    memory: "memory-with",
    spontaneous: "spont-yes",
    increment: "increment-prop",
  },

  {
    // checked
    id: "in_reg_wt_mem_spnone_selfneg_and",
    label:
      "Single weighted inverted negative w/ negative self-regulation (AND)",
    type: "inverted-negative",
    strength: "strength-regular",
    weight: "weight-yes",
    memory: "memory-with",
    spontaneous: "spont-none",
    increment: "increment-prop",
  },

  {
    // checked
    id: "in_reg_wt_mem_spyes_selfneg_and",
    label:
      "Single weighted inverted negative w/ negative self-regulation (AND) and w/ spontaneous",
    type: "inverted-negative",
    strength: "strength-regular",
    weight: "weight-yes",
    memory: "memory-with",
    spontaneous: "spont-yes",
    increment: "increment-prop",
  },

  {
    // checked
    id: "in_weak_none_mem_spnone_selfneg_or",
    label: "Single weak inverted negative w/ negative self-regulation (OR)",
    type: "inverted-negative",
    strength: "strength-weak",
    weight: "weight-none",
    memory: "memory-without",
    spontaneous: "spont-none",
    increment: "increment-prop",
  },

  {
    // checked
    id: "in_weak_none_mem_spyes_selfneg_or",
    label:
      "Single weak inverted negative w/ negative self-regulation (OR) and w/ spontaneous",
    type: "inverted-negative",
    strength: "strength-weak",
    weight: "weight-none",
    memory: "memory-with",
    spontaneous: "spont-yes",
    increment: "increment-prop",
  },
  {
    // checked
    id: "in_weak_none_nomem_spnone_selfneg_and",
    label: "Single weak inverted negative w/ negative self-regulation (AND)",
    type: "inverted-negative",
    strength: "strength-weak",
    weight: "weight-none",
    memory: "memory-without",
    spontaneous: "spont-none",
    increment: "increment-prop",
  },

  {
    // checked
    id: "in_weak_none_mem_spyes_selfneg_and",
    label:
      "Single weak inverted negative w/ negative self-regulation (AND) and w/ spontaneous",
    type: "inverted-negative",
    strength: "strength-weak",
    weight: "weight-none",
    memory: "memory-with",
    spontaneous: "spont-yes",
    increment: "increment-prop",
  },
].map((m) => ({ ...m, card: makeCard(m, CARD_OVERRIDES[m.id]) }));

/* ─────────────────────────────────────────────────────────────
   CONSTANTS
   ───────────────────────────────────────────────────────────── */
export const LEVEL_KEYS = new Set([
  "single-regulator:levels-2",
  "single-regulator:levels-3",
  "single-regulator:levels-4",
  "single-regulator:levels-5",
]);
